import { Controller } from '@hotwired/stimulus'
import {
  ACCESS_CHANGE_HEADING,
  ACCESS_EVERYONE_ELSE_LOSES_SIGHT,
  ACCESS_EVERYONE_WITH_FORMS_ACCESS,
  ACCESS_GAINS_SIGHT,
  ACCESS_LOSES_SIGHT,
  ACCESS_NO_TEAMS_WARNING,
  ACCESS_ONLY_THESE_TEAMS_CAN_READ,
  ACCESS_OPENS_TO_EVERYONE,
  ACCESS_SUBMISSION_COUNT_MANY,
  ACCESS_SUBMISSION_COUNT_ONE,
  SELECT_TEAMS,
  trans,
} from '../translator'

const TEAMS = 'teams'

interface ConsequenceLine {
  text: string
  emphasis?: string
}

interface SemanticDropdown {
  dropdown?: (settings: Record<string, unknown>) => void
}

declare const jQuery: ((element: Element) => SemanticDropdown) & { fn: SemanticDropdown }

export default class AccessConsequenceController extends Controller<HTMLElement> {
  static targets = ['mode', 'teams', 'teamsWrap', 'always', 'consequence', 'save']
  static values = { mode: String, teams: Array, submissions: Number }

  declare readonly modeTargets: HTMLInputElement[]
  declare readonly teamsTarget: HTMLSelectElement
  declare readonly hasTeamsTarget: boolean
  declare readonly teamsWrapTarget: HTMLElement
  declare readonly hasTeamsWrapTarget: boolean
  declare readonly alwaysTarget: HTMLElement
  declare readonly hasAlwaysTarget: boolean
  declare readonly consequenceTarget: HTMLElement
  declare readonly hasConsequenceTarget: boolean
  declare readonly saveTarget: HTMLButtonElement
  declare readonly hasSaveTarget: boolean
  declare readonly modeValue: string
  declare readonly teamsValue: string[]
  declare readonly submissionsValue: number

  private readonly onChange = (): void => this.refresh()

  connect (): void {
    this.element.addEventListener('change', this.onChange)
    this.enhanceTeamsPicker()
    this.refresh()
  }

  private enhanceTeamsPicker (): void {
    if (!this.hasTeamsTarget || typeof jQuery === 'undefined' || undefined === jQuery.fn.dropdown) {
      return
    }

    jQuery(this.teamsTarget).dropdown?.({
      fullTextSearch: true,
      match: 'text',
      placeholder: trans(SELECT_TEAMS, {}, 'form-types'),
      onChange: this.onChange,
    })
  }

  disconnect (): void {
    this.element.removeEventListener('change', this.onChange)
  }

  refresh (): void {
    const mode = this.chosenMode()
    const teamIds = this.chosenTeamIds()

    if (this.hasTeamsTarget) {
      this.teamsTarget.disabled = TEAMS !== mode
      this.teamsTarget.closest('.ui.dropdown')?.classList.toggle('disabled', TEAMS !== mode)
    }

    if (this.hasAlwaysTarget) {
      this.alwaysTarget.hidden = TEAMS !== mode
    }

    const changed = mode !== this.modeValue || (TEAMS === mode && !this.sameTeams(teamIds))

    this.render(mode, teamIds, changed)

    if (this.hasSaveTarget) {
      this.saveTarget.disabled = !changed
      const destructive = changed && this.takesSightAway(mode, teamIds)

      this.saveTarget.classList.toggle('ft-btn-danger', destructive)
      this.saveTarget.classList.toggle('ft-btn-filled', !destructive)
      this.saveTarget.classList.toggle('btn-raven-reg', !destructive)
    }
  }

  private takesSightAway (mode: string, teamIds: string[]): boolean {
    if (mode !== this.modeValue) {
      return true
    }

    return this.teamsValue.some((id) => !teamIds.includes(id))
  }

  private render (mode: string, teamIds: string[], changed: boolean): void {
    if (!this.hasConsequenceTarget) {
      return
    }

    this.consequenceTarget.textContent = ''

    if (!changed) {
      return
    }

    const points = document.createElement('ul')

    for (const line of this.lines(mode, teamIds)) {
      points.appendChild(this.point(line))
    }

    if (!points.hasChildNodes()) {
      return
    }

    const heading = document.createElement('p')

    heading.textContent = trans(ACCESS_CHANGE_HEADING, {}, 'form-types')

    this.consequenceTarget.appendChild(heading)
    this.consequenceTarget.appendChild(points)
  }

  private point (line: ConsequenceLine): HTMLLIElement {
    const point = document.createElement('li')

    if (undefined === line.emphasis) {
      point.textContent = line.text

      return point
    }

    const [before, after] = line.text.split('%bold%')
    const emphasis = document.createElement('strong')

    emphasis.textContent = line.emphasis

    point.appendChild(document.createTextNode(before ?? ''))
    point.appendChild(emphasis)
    point.appendChild(document.createTextNode(after ?? ''))

    return point
  }

  private lines (mode: string, teamIds: string[]): ConsequenceLine[] {
    const held = { '%submissions%': this.submissionsHeld() }

    if (TEAMS !== mode) {
      return [{
        text: trans(ACCESS_OPENS_TO_EVERYONE, held, 'form-types'),
        emphasis: trans(ACCESS_EVERYONE_WITH_FORMS_ACCESS, {}, 'form-types'),
      }]
    }

    const noTeams = { text: trans(ACCESS_NO_TEAMS_WARNING, {}, 'form-types') }

    if (TEAMS !== this.modeValue) {
      if (teamIds.length === 0) {
        return [noTeams]
      }

      return [
        {
          text: trans(ACCESS_ONLY_THESE_TEAMS_CAN_READ, held, 'form-types'),
          emphasis: teamIds.map((id) => this.teamName(id)).join(' and '),
        },
        { text: trans(ACCESS_EVERYONE_ELSE_LOSES_SIGHT, {}, 'form-types') },
      ]
    }

    const lines: ConsequenceLine[] = []

    for (const id of this.teamsValue) {
      if (!teamIds.includes(id)) {
        lines.push({ text: trans(ACCESS_LOSES_SIGHT, held, 'form-types'), emphasis: this.teamName(id) })
      }
    }

    for (const id of teamIds) {
      if (!this.teamsValue.includes(id)) {
        lines.push({ text: trans(ACCESS_GAINS_SIGHT, held, 'form-types'), emphasis: this.teamName(id) })
      }
    }

    if (teamIds.length === 0) {
      lines.push(noTeams)
    }

    return lines
  }

  private submissionsHeld (): string {
    const held = this.submissionsValue

    return held === 1
      ? trans(ACCESS_SUBMISSION_COUNT_ONE, {}, 'form-types')
      : trans(ACCESS_SUBMISSION_COUNT_MANY, { '%count%': String(held) }, 'form-types')
  }

  private teamName (id: string): string {
    if (!this.hasTeamsTarget) {
      return id
    }

    const option = this.teamsTarget.querySelector(`option[value="${id}"]`)

    return option?.textContent?.trim() ?? id
  }

  private chosenMode (): string {
    return this.modeTargets.find((input) => input.checked)?.value ?? this.modeValue
  }

  private chosenTeamIds (): string[] {
    if (!this.hasTeamsTarget) {
      return []
    }

    return Array.from(this.teamsTarget.selectedOptions).map((option) => option.value)
  }

  private sameTeams (teamIds: string[]): boolean {
    if (teamIds.length !== this.teamsValue.length) {
      return false
    }

    return teamIds.every((id) => this.teamsValue.includes(id))
  }
}
